/**
 * THE PLAN GATE (PRODUCT.md §3) — a room that has been talking/working chooses to PLAN: draft milestones FROM THE
 * ROOM'S CONTEXT (what's known, what's still missing), edit them together, approve → the room's plan; the room moves
 * to the Plan stage (Plan / Run / Verify, the Tasks tab). Milestones are never set up front — only here, when asked.
 *
 *   <PlanGateDialog open plan={roomPlan} onPlan={(req) => Promise<{roomPlan, missing?}>} onClose={…} />
 *   req = {action: 'propose', intent} | {action: 'suggested'} | {action: 'draft', milestones, goal} | {action: 'approve', milestones}
 *
 * THE SUGGESTED PLAN (owner 2026-10-21): when the network's build drafted one (``plan.suggested`` = {milestones:
 * [{name, milestone, taskCount}], taskCount}), it is OFFERED — "Use suggested plan" fills the draft (then change
 * anything: rename, drop, add, reorder), or ignore it and draft from context / write your own. On approve, the
 * suggested tasks of the milestones kept come along as drafts. It's a starting point, never a rule.
 */
import { usePlanWords } from '../utils/planWords.js';
import React, { useEffect, useState } from 'react';
import { Loader2, Plus, X, Sparkles } from 'lucide-react';

const blank = () => ({ name: '', milestone: '' });

export default function PlanGateDialog({ open, plan = null, onPlan, onClose }) {
  const w = usePlanWords();   // E1 — Milestone, or the network's own word (an Expert Network: Team)
  const [intent, setIntent] = useState('');
  const [rows, setRows] = useState([]);
  const [goal, setGoal] = useState('');
  const [missing, setMissing] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [fromSuggested, setFromSuggested] = useState(false);
  useEffect(() => {
    if (!open) return;
    const start = (plan && (plan.draft?.length ? plan.draft : plan.milestones)) || [];
    setRows(start.length ? start.map((m) => ({ name: m.name || '', milestone: m.milestone || '' })) : []);
    setGoal((plan && plan.goal) || '');
    setMissing([]); setError(''); setBusy('');
    setFromSuggested(!!(plan && plan.source === 'suggested' && plan.draft?.length));
  }, [open, plan]);
  if (!open) return null;
  const run = async (action, extra = {}) => {
    setBusy(action); setError('');
    try {
      const r = await onPlan({ action, ...extra });
      const p = (r && r.roomPlan) || {};
      if (action === 'approve') { onClose?.(); return; }
      const ms = p.draft && p.draft.length ? p.draft : (p.milestones || []);
      setRows(ms.map((m) => ({ name: m.name || '', milestone: m.milestone || '' })));
      setGoal(p.goal || goal);
      setFromSuggested(p.source === 'suggested');
      if (Array.isArray(r?.missing)) setMissing(r.missing);
    } catch (e) {
      setError((e && e.message) || "Couldn't do that just now — try again.");
    }
    setBusy('');
  };
  const clean = rows.filter((r) => r.name.trim());
  const suggested = plan && plan.suggested && Array.isArray(plan.suggested.milestones)
    && plan.suggested.milestones.length ? plan.suggested : null;
  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="plan-gate"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-3">
          <h2 className="text-base font-semibold text-gray-900">Plan this room</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {w.groups} are drafted from what this room already knows (its context). Edit them, then approve — the room
            moves to Plan: {w.leavesLc}, runs and verification.
          </p>
        </div>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-4">
          <div className="flex gap-2">
            <input data-testid="plan-gate-intent" value={intent} onChange={(e) => setIntent(e.target.value)}
              placeholder="What do you want to plan? (optional — the context is used either way)"
              className="min-w-0 flex-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs" />
            <button type="button" data-testid="plan-gate-propose" disabled={!!busy}
              onClick={() => run('propose', { intent })}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">
              {busy === 'propose' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Sparkles className="h-3.5 w-3.5" aria-hidden />}
              Draft from context
            </button>
          </div>
          {suggested && !rows.length ? (
            <div className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2" data-testid="plan-gate-suggested">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium text-gray-800">Suggested plan</p>
                  <p className="text-[11px] text-gray-500">
                    The network suggests {suggested.milestones.length} {suggested.milestones.length === 1 ? w.groupLc : w.groupsLc}
                    {suggested.taskCount ? ` with ${suggested.taskCount} ${w.leavesLc}` : ''}. Use it as a start and change
                    anything — or draft from context, or write your own.
                  </p>
                  <ol className="mt-1 list-decimal pl-4 text-[11px] text-gray-700">
                    {suggested.milestones.map((m) => (
                      <li key={m.name}>{m.name}{m.taskCount ? <span className="text-gray-400"> · {m.taskCount}</span> : null}</li>
                    ))}
                  </ol>
                </div>
                <button type="button" data-testid="plan-gate-use-suggested" disabled={!!busy}
                  onClick={() => run('suggested')}
                  className="shrink-0 rounded-md bg-sky-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-sky-700 disabled:opacity-50">
                  {busy === 'suggested' ? 'Loading…' : 'Use suggested plan'}
                </button>
              </div>
            </div>
          ) : null}
          {fromSuggested && rows.length ? (
            <p className="text-[11px] text-sky-800" data-testid="plan-gate-from-suggested">
              From the network's suggested plan — change anything. The suggested {w.leavesLc} come with the {w.groupsLc} you keep.
            </p>
          ) : null}
          {goal ? <p className="text-[12px] text-gray-700" data-testid="plan-gate-goal"><span className="font-medium">Goal:</span> {goal}</p> : null}
          <ol className="space-y-1.5" data-testid="plan-gate-rows">
            {rows.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="mt-1 w-5 shrink-0 text-right text-[11px] text-gray-400">{i + 1}.</span>
                <input aria-label={`Milestone ${i + 1} name`} value={r.name} onChange={(e) => set(i, 'name', e.target.value)}
                  placeholder={w.group} className="w-36 shrink-0 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium" />
                <input aria-label={`Milestone ${i + 1} exit criterion`} value={r.milestone}
                  onChange={(e) => set(i, 'milestone', e.target.value)} placeholder="Done when…"
                  className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs" />
                <button type="button" aria-label={`Remove milestone ${i + 1}`} onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                  className="mt-0.5 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ol>
          <button type="button" data-testid="plan-gate-add" onClick={() => setRows((rs) => [...rs, blank()])}
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-indigo-700 hover:bg-indigo-50">
            <Plus className="h-3.5 w-3.5" aria-hidden /> Add {w.groupLc}
          </button>
          {missing.length ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="plan-gate-missing">
              <div className="font-medium">The context doesn&apos;t say yet:</div>
              <ul className="ml-4 list-disc">{missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          ) : null}
          {error ? <div className="text-[11px] text-rose-600" data-testid="plan-gate-error">{error}</div> : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" onClick={onClose} className="px-2.5 py-1 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100 transition-colors">Cancel</button>
          <button type="button" data-testid="plan-gate-save" disabled={!!busy || !clean.length}
            onClick={() => run('draft', { milestones: clean, goal })}
            className="px-2.5 py-1 text-xs font-medium rounded-md text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50">
            Save draft
          </button>
          <button type="button" data-testid="plan-gate-approve" disabled={!!busy || !clean.length}
            onClick={() => run('approve', { milestones: clean })}
            className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50">
            {busy === 'approve' ? 'Approving…' : 'Approve plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
